import { useState } from 'react';
import { ChevronDown, Check, Layers, Search } from 'lucide-react';
import { Input, Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, cn } from '@mlm/ui';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { fmtDate } from '../lib/format';

/**
 * Phase 13: switch the active-slot context. With a single active slot it renders
 * a non-interactive identity pill (no menu); with 2+ it opens a bottom-sheet on
 * mobile (auto via Sheet) and a popover-like sheet on desktop too. Hidden only
 * when the caller has no active slots.
 *
 * `slotBadges` maps slot id → count of orders needing action on that slot, and
 * `otherCount` is the total on slots OTHER than the active one. When `otherCount`
 * is > 0 the trigger shows a red dot and each row shows its own count, so a member
 * viewing the wrong slot is directed to where the order actually is.
 */
export function SwitchSlotMenu({
  slotBadges = {},
  otherCount = 0,
}: {
  slotBadges?: Record<string, number>;
  otherCount?: number;
} = {}) {
  const { activeSlotsWithLabel, activeSlot, activeSlotLabel, setActiveSlotId } = useMySlots();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  if (activeSlotsWithLabel.length === 0) return null;

  if (activeSlotsWithLabel.length === 1) {
    return (
      <div className="flex items-center gap-2 rounded-md px-2 py-1.5" aria-label="Active slot">
        <Layers className="h-3.5 w-3.5 text-primary" />
        <span className="text-sm font-medium leading-none">{activeSlotLabel}</span>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? activeSlotsWithLabel.filter(
        (s) =>
          s.slotCode.toLowerCase().includes(q) || s.label.toLowerCase().includes(q),
      )
    : activeSlotsWithLabel;

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery(''); // reset the filter each time the sheet closes
      }}
    >
      <SheetTrigger asChild>
        <button
          className="relative flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-muted"
          aria-label={
            otherCount > 0
              ? 'Switch active slot (another slot has orders needing action)'
              : 'Switch active slot'
          }
        >
          <span className="relative flex-none">
            <Layers className="h-3.5 w-3.5 text-primary" />
            {otherCount > 0 && (
              <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-card" />
            )}
          </span>
          <span className="text-sm font-medium leading-none">{activeSlotLabel}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </SheetTrigger>
      {/* Flex column so the header + search stay pinned and the list scrolls —
          accounts can hold many slots (the list would otherwise run off-screen). */}
      <SheetContent side="right" className="flex w-80 flex-col gap-0">
        <SheetHeader>
          <SheetTitle>Switch slot</SheetTitle>
        </SheetHeader>
        <p className="mt-1 text-xs text-muted-foreground">
          Each slot has its own wallet, genealogy, orders, and payouts.
        </p>
        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search slot code…"
            aria-label="Search slots"
            className="pl-8"
          />
        </div>
        <div className="mt-2 px-0.5 text-[11px] text-muted-foreground">
          {filtered.length} of {activeSlotsWithLabel.length} slots
        </div>
        <ul className="-mx-6 mt-1 min-h-0 flex-1 space-y-1 overflow-y-auto px-6">
          {filtered.map((s) => {
            const selected = s.id === activeSlot?.id;
            const badge = slotBadges[s.id] ?? 0;
            return (
              <li key={s.id}>
                <button
                  onClick={() => {
                    setActiveSlotId(s.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-center justify-between gap-2 rounded-md px-3 py-2.5 text-sm text-left hover:bg-muted',
                    selected && 'bg-primary/10 text-primary',
                  )}
                >
                  <div className="min-w-0">
                    <div className="font-medium">{s.label}</div>
                    <div className="text-xs text-muted-foreground font-mono">{s.slotCode}</div>
                    <div className="text-xs text-muted-foreground">
                      Activated {fmtDate(s.activatedAt)}
                    </div>
                  </div>
                  <div className="flex flex-none items-center gap-2">
                    {badge > 0 && (
                      <span
                        className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10.5px] font-semibold leading-none text-white"
                        aria-label={`${badge} order${badge === 1 ? '' : 's'} need attention`}
                      >
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                    {selected && <Check className="h-4 w-4" />}
                  </div>
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              No slots match “{query.trim()}”.
            </li>
          )}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
